import React, { useEffect, useState } from "react";
import {
  alpha,
  Breadcrumbs,
  Chip,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import dayjs from "dayjs";
import { formatLocalDateTime } from "../../utils/datetime";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { docAmounts } from "../../utils/docAmounts";
import { Link, useNavigate, useParams } from "react-router-dom";
import Layout from "../../layout/Layout";
import { apiData } from "../../services/api_service";
import { proformaQuoteBaseAPI } from "../../config";
import { formatAED, formatNumber } from "../../utils/format";
import { getDocPaymentStatus } from "../../utils/docPaymentStatus";

// Same wording as the Proforma list page.
const PAYMENT_TYPE_LABELS = { 1: "Credit", 2: "Immediate" };
const PAYMENT_METHOD_LABELS = {
  1: "Cash",
  2: "Card",
  3: "Cheque",
  5: "Bank Transfer",
  6: "Online Payment",
  7: "Bill to Company",
};
const formatPaymentMethod = (value) =>
  value === null || value === undefined || value === ""
    ? "-"
    : PAYMENT_METHOD_LABELS[Number(value)] || String(value);
const STATUS_MAP = {
  active: { label: "Active", color: "warning" },
  converted: { label: "Converted", color: "success" },
  cancelled: { label: "Cancelled", color: "error" },
  partial_returned: { label: "Partial Returned", color: "warning" },
  fully_returned: { label: "Fully Returned", color: "info" },
};
const fmtDay = (v) => (v ? dayjs(v).format("DD-MM-YYYY") : "-");
// A money cell: AED amount, or "-" for a zero when the list shows it that way.
const money = (v, zeroAsDash = false) => {
  const n = Number(v) || 0;
  return zeroAsDash && n === 0 ? "-" : formatAED(n);
};

const parseItems = (items) => {
  if (Array.isArray(items)) return items;
  try {
    return JSON.parse(items || "[]");
  } catch (error) {
    return [];
  }
};

// "Name (SKU) x qty" per line, as in the list's Ordered Products column.
const itemLines = (items) =>
  parseItems(items).map(
    (l) => `${l.product_name || l.product_id || "Product"} (${l.sku || "-"}) x ${formatNumber(l.quantity)}`,
  );

const versionLabel = (version) => {
  const value = version.display_version ?? version.version;
  const numericValue = Number(value);
  if (Number.isFinite(numericValue)) return `v${numericValue}`;
  return value ? `v${value}` : "-";
};

function ProformaQuoteHistory() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [current, setCurrent] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchVersions = async () => {
    setLoading(true);
    try {
      const result = await apiData("get", `${proformaQuoteBaseAPI}/${id}/versions`);
      if (result?.success) {
        setCurrent(result.data?.current || null);
        setVersions(result.data?.versions || []);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVersions();
  }, [id]);

  // Item 3: Export the Proforma history shown below to an Excel file, using the
  // same table's rows (current + every past version).
  const handleExportHistory = async () => {
    if (!rows.length) {
      alert("No history to export!");
      return;
    }

    const AED = '"AED" #,##0.00';
    const num = (v) => Math.round((parseFloat(v) || 0) * 100) / 100;
    const fmtDate = (v) => (v ? dayjs(v).format("DD-MM-YYYY") : "");
    const border = { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } };
    const style = (sheet, moneyKeys, extra = {}) => {
      moneyKeys.forEach((k) => (sheet.getColumn(k).numFmt = AED));
      Object.entries(extra).forEach(([k, f]) => (sheet.getColumn(k).numFmt = f));
      sheet.views = [{ state: "frozen", ySplit: 1 }];
      sheet.eachRow((row, n) => {
        row.eachCell((cell) => {
          cell.border = border;
          cell.alignment =
            n === 1
              ? { vertical: "middle", horizontal: "center", wrapText: true }
              : { vertical: "top", horizontal: typeof cell.value === "number" ? "right" : "left", wrapText: true };
        });
      });
      sheet.getRow(1).font = { bold: true };
    };

    const workbook = new ExcelJS.Workbook();
    const ws = workbook.addWorksheet("Proforma History");
    ws.columns = [
      // History columns first, then the same columns, in the same order, as the Proforma list.
      { header: "#", key: "index", width: 6 },
      { header: "Proforma No", key: "number", width: 18 },
      { header: "Version", key: "version", width: 10 },
      { header: "Updated At", key: "when", width: 18 },
      { header: "Updated By", key: "by", width: 20 },
      { header: "Date", key: "date", width: 13 },
      { header: "Bill To Company", key: "customer", width: 28 },
      { header: "Phone", key: "phone", width: 16 },
      { header: "Email", key: "email", width: 26 },
      { header: "Store", key: "store", width: 22 },
      { header: "Product Name", key: "products", width: 40 },
      { header: "SKU", key: "skus", width: 16 },
      { header: "Quantity", key: "qtys", width: 10 },
      { header: "Unit Price (Excl. Tax)", key: "unit_ex", width: 16 },
      { header: "Unit Price (Incl. Tax)", key: "unit_inc", width: 16 },
      { header: "Discount %", key: "disc_pct", width: 11 },
      { header: "Discount Amount", key: "disc_amt", width: 15 },
      { header: "Taxable Value", key: "taxable", width: 15 },
      { header: "Product VAT", key: "line_vat", width: 13 },
      { header: "Line Total (Incl. Tax)", key: "line_total", width: 17 },
      { header: "Subtotal", key: "subtotal", width: 18 },
      { header: "Discount", key: "discount", width: 18 },
      { header: "VAT", key: "vat", width: 18 },
      { header: "Shipping", key: "shipping", width: 18 },
      { header: "Grand Total", key: "grand", width: 18 },
      { header: "Advance Received", key: "advance", width: 20 },
      { header: "Balance Due", key: "balance_due", width: 18 },
      { header: "Payment Status", key: "pay_status", width: 18 },
      { header: "Due Date", key: "due_date", width: 13 },
      { header: "Payment Type", key: "pay_type", width: 16 },
      { header: "Payment Method", key: "method", width: 18 },
      { header: "Payment Date", key: "payment_date", width: 14 },
      { header: "Transaction Id", key: "txn", width: 20 },
      { header: "Payment Attachment", key: "attachment", width: 22 },
      { header: "Created By", key: "created_by", width: 18 },
      { header: "Created At", key: "created_at", width: 14 },
      { header: "Last Updated", key: "updated_at", width: 14 },
      { header: "Status", key: "status", width: 15 },
      { header: "TRN / VAT No", key: "trn", width: 18 },
      { header: "Address", key: "address", width: 30 },
      { header: "Emirate", key: "emirate", width: 14 },
      { header: "PO Box", key: "po_box", width: 12 },
      { header: "PDF", key: "pdf", width: 14 },
    ];

    rows.forEach((row, index) => {
      const lines = parseItems(row.items);
      const when = row.__when ? formatLocalDateTime(row.__when) : "";
      const number = current?.proforma_number || "";
      const isCurrent = !!row.__current;
      const a = docAmounts(row);
      // Prices are saved as typed (tax-inclusive for an Inclusive document); the
      // rate is the saved one, else worked back from the saved VAT.
      const inclusive = (row.tax_mode ?? current?.tax_mode) === "inclusive";
      const taxBase = Math.max(0, num(row.grand_total) - num(row.tax_amount) - num(row.shipping_amount));
      const taxRate =
        parseFloat(row.tax_type ?? current?.tax_type) ||
        (taxBase > 0 && num(row.tax_amount) > 0 ? Math.round((num(row.tax_amount) / taxBase) * 10000) / 100 : 0);
      const lineVals = lines.map((l) => {
        const exF = inclusive ? 1 / (1 + taxRate / 100) : 1;
        const unitEx = inclusive ? num(l.selling_price) / (1 + taxRate / 100) : num(l.selling_price);
        const gross = unitEx * (parseFloat(l.quantity) || 0);
        const disc = num(l.discount_amount) * exF;
        return {
          unit_ex: num(unitEx),
          unit_inc: num(unitEx * (1 + taxRate / 100)),
          disc_pct: gross > 0 ? Math.round((disc / gross) * 10000) / 100 : 0,
          disc_amt: num(disc),
          taxable: num(num(l.total_price) * exF),
          line_vat: num(num(l.total_price) * exF * (taxRate / 100)),
          line_total: num(num(l.total_price) * exF * (1 + taxRate / 100)),
        };
      });

      // The version's money is written once (on its first product row) so the
      // column totals still add up; every other column repeats on each row.
      const versionMoney = {
        subtotal: a.subtotal,
        discount: a.discount,
        vat: a.vat,
        shipping: a.shipping,
        grand: a.grand,
        // Payment figures are only known for the live document, not for past versions.
        advance: isCurrent ? num(row.advance_unapplied) : "",
        balance_due: isCurrent ? num(row.balance_due) : "",
      };
      const versionRow = {
        index: index + 1,
        number,
        version: row.__label,
        when,
        by: row.__byName || row.__by || "",
        date: fmtDate(row.order_date),
        customer: row.customer_name || "",
        phone: row.customer_phone || "",
        email: row.customer_email || "",
        // Everything below is only known for the live document (a past version keeps
        // just the customer, items and amounts), so past versions leave it blank.
        store: isCurrent ? row.store_name || "" : "",
        pay_status: isCurrent ? getDocPaymentStatus(row).label : "",
        due_date: isCurrent ? fmtDate(row.due_date) : "",
        pay_type: isCurrent ? PAYMENT_TYPE_LABELS[Number(row.payment_type)] || "" : "",
        method: isCurrent ? (String(row.payment_type) === "1" ? "Credit" : formatPaymentMethod(row.payment_method).replace(/^-$/, "")) : "",
        payment_date: isCurrent ? fmtDate(row.last_payment_date) : "",
        txn: isCurrent ? row.payment_reference_no || "" : "",
        attachment:
          isCurrent && row.payment_attachment_url
            ? { text: row.payment_attachment_name || "View file", hyperlink: row.payment_attachment_url }
            : "",
        created_by: isCurrent ? row.created_by_name || "" : "",
        created_at: isCurrent ? fmtDate(row.created_at) : "",
        updated_at: isCurrent ? fmtDate(row.updated_at) : "",
        status: isCurrent ? row.status || "active" : "Previous version",
        trn: row.trn_number || row.vat_no || "",
        address: row.address || "",
        emirate: row.emirate || "",
        po_box: row.po_box || "",
        pdf: row.pdf_url ? { text: "View PDF", hyperlink: row.pdf_url } : "",
      };
      // One block per version: only Product / SKU / Quantity get a row per
      // product; every other cell is merged down across the version's products.
      const startRow = ws.rowCount + 1;
      (lines.length ? lines : [null]).forEach((l, li) =>
        ws.addRow({
          ...versionRow,
          products: l ? l.product_name || l.product_id || "Product" : "",
          skus: l ? l.sku || "" : "",
          qtys: l ? parseFloat(l.quantity) || 0 : "",
          ...(l ? lineVals[li] : {}),
          ...(li === 0 ? versionMoney : {}),
        }),
      );
      if (ws.rowCount > startRow) {
        ws.columns.forEach((col) => {
          if (!["products", "skus", "qtys", "unit_ex", "unit_inc", "disc_pct", "disc_amt", "taxable", "line_vat", "line_total"].includes(col.key)) ws.mergeCells(startRow, col.number, ws.rowCount, col.number);
        });
      }
    });

    style(ws, ["unit_ex", "unit_inc", "disc_amt", "taxable", "line_vat", "line_total", "subtotal", "discount", "vat", "shipping", "grand", "advance", "balance_due"]);
    ws.getColumn("disc_pct").numFmt = '0.00"%"';

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), `Proforma_History_${current?.proforma_number || id}_${dayjs().format("DD-MM-YYYY")}.xlsx`);
  };

  const rows = [
    ...(current
      ? [
          {
            ...current,
            __label: "Current",
            __current: true,
            __when: current.updated_at || current.created_at,
            __byName: current.updated_by_name || current.created_by_name,
            __by: current.updated_by || current.created_by,
          },
        ]
      : []),
    ...versions.map((version) => ({
      ...version,
      __label: versionLabel(version),
      __when: version.created_at,
      __byName: version.edited_by_name,
      __by: version.edited_by,
    })),
  ];

  // Same columns, in the same order, as the Proforma list page. History-only
  // columns (Version, Updated At / By, PDF) are added around them. Payment and
  // store details exist only for the live document — a past version shows "-".
  const NOWRAP = { whiteSpace: "nowrap" };
  const columns = [
    {
      h: "Version",
      render: (row) => (
        <Chip label={row.__label} color={row.__label === "Current" ? "success" : "default"} size="small" />
      ),
    },
    { h: "Updated At", nowrap: true, render: (row) => (row.__when ? formatLocalDateTime(row.__when) : "-") },
    {
      h: "Updated By",
      render: (row) => (
        <>
          <Typography variant="body2">{row.__byName || row.__by || "-"}</Typography>
          {row.__byName && row.__by ? (
            <Typography variant="caption" color="text.secondary">
              User ID: {row.__by}
            </Typography>
          ) : null}
        </>
      ),
    },
    { h: "Proforma No", nowrap: true, render: () => current?.proforma_number || "-" },
    { h: "Date", nowrap: true, render: (row) => fmtDay(row.order_date) },
    { h: "Bill To Company", render: (row) => row.customer_name || "-" },
    { h: "Phone", nowrap: true, render: (row) => row.customer_phone || "-" },
    { h: "Email", render: (row) => row.customer_email || "-" },
    { h: "Store", render: (row) => (row.__current ? row.store_name || "-" : "-") },
    {
      h: "Ordered Products",
      render: (row) => {
        const lines = itemLines(row.items);
        if (!lines.length) return "-";
        return lines.map((l, i) => <div key={i}>{l}</div>);
      },
    },
    {
      h: "Qty",
      render: (row) => {
        const t = parseItems(row.items).reduce((s, l) => s + (parseFloat(l.quantity) || 0), 0);
        return t ? formatNumber(t) : "-";
      },
    },
    { h: "Subtotal", align: "right", nowrap: true, render: (row) => money(docAmounts(row).subtotal) },
    { h: "Discount", align: "right", nowrap: true, render: (row) => money(docAmounts(row).discount, true) },
    { h: "VAT", align: "right", nowrap: true, render: (row) => money(docAmounts(row).vat, true) },
    { h: "Shipping", align: "right", nowrap: true, render: (row) => money(docAmounts(row).shipping, true) },
    { h: "Grand Total", align: "right", nowrap: true, render: (row) => money(row.grand_total) },
    {
      h: "Balance Due",
      align: "right",
      nowrap: true,
      render: (row) => (row.__current ? money(row.balance_due) : "-"),
    },
    {
      h: "Received",
      align: "right",
      nowrap: true,
      render: (row) =>
        row.__current ? (
          <>
            {money(row.advance_unapplied)}
            {row.advance_modes ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                {row.advance_modes}
              </Typography>
            ) : null}
          </>
        ) : (
          "-"
        ),
    },
    {
      h: "Payment Status",
      nowrap: true,
      render: (row) => (row.__current ? getDocPaymentStatus(row).label : "-"),
    },
    {
      h: "Due Date",
      nowrap: true,
      render: (row) => {
        if (!row.__current || !row.due_date) return "-";
        const days = Number(row.credit_days || 0);
        return (
          <>
            {fmtDay(row.due_date)}
            {days > 0 ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                {days} credit days
              </Typography>
            ) : null}
          </>
        );
      },
    },
    {
      h: "Payment Type",
      nowrap: true,
      render: (row) => (row.__current ? PAYMENT_TYPE_LABELS[Number(row.payment_type)] || "-" : "-"),
    },
    {
      h: "Payment Method",
      nowrap: true,
      render: (row) =>
        row.__current && String(row.payment_type) !== "1" ? formatPaymentMethod(row.payment_method) : "-",
    },
    { h: "Payment Date", nowrap: true, render: (row) => (row.__current ? fmtDay(row.last_payment_date) : "-") },
    { h: "Transaction Id", nowrap: true, render: (row) => (row.__current ? row.payment_reference_no || "-" : "-") },
    {
      h: "Payment Attachment",
      nowrap: true,
      render: (row) =>
        row.__current && row.payment_attachment_url ? (
          <a href={row.payment_attachment_url} target="_blank" rel="noopener noreferrer">
            View File
          </a>
        ) : (
          "-"
        ),
    },
    {
      h: "Status",
      nowrap: true,
      render: (row) => {
        if (!row.__current) return "Previous version";
        const { label, color } = STATUS_MAP[row.status || "active"] || STATUS_MAP.active;
        return (
          <Chip
            size="small"
            label={label}
            sx={(theme) => ({
              bgcolor: alpha(theme.palette[color].main, 0.12),
              color: theme.palette[color].dark,
              fontWeight: 700,
              fontSize: 11,
              letterSpacing: 0.4,
              textTransform: "uppercase",
              border: "none",
            })}
          />
        );
      },
    },
    { h: "Created By", render: (row) => (row.__current ? row.created_by_name || "-" : "-") },
    { h: "Created At", nowrap: true, render: (row) => (row.__current ? fmtDay(row.created_at) : "-") },
    { h: "Last Updated", nowrap: true, render: (row) => (row.__current ? fmtDay(row.updated_at) : "-") },
    {
      h: "PDF",
      align: "center",
      render: (row) =>
        row.pdf_url ? (
          <a href={row.pdf_url} target="_blank" rel="noreferrer">
            <RemoveRedEyeIcon color="primary" />
          </a>
        ) : (
          "-"
        ),
    },
  ];

  return (
    <Layout>
      <Grid className="employee-form bold-header">
        <Grid container spacing={3} className="mb-3">
          <Grid item xs={12} md={8}>
            <Typography className="page_title" textAlign="left">
              Proforma Invoice History - {current?.proforma_number || ""}
            </Typography>
            <Breadcrumbs aria-label="breadcrumb">
              <Link underline="hover" color="inherit" to="/dashboard">
                Dashboard
              </Link>
              <Link underline="hover" color="inherit" to="/proforma-quote">
                Proforma Invoice
              </Link>
              <Typography color="text.primary">History</Typography>
            </Breadcrumbs>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              flexWrap: "wrap",
              gap: 1.5,
            }}
          >
            <LoadingButton
              size="small"
              variant="outlined"
              startIcon={<FileDownloadIcon sx={{ fontSize: 16 }} />}
              onClick={handleExportHistory}
              disabled={loading || rows.length === 0}
              sx={{
                borderRadius: 1,
                px: 2,
                py: 0.5,
                fontWeight: 600,
                fontSize: 13,
                lineHeight: 1.4,
                textTransform: "none",
                borderColor: "#c9d4e3",
                color: "#0c54a0",
                "&:hover": {
                  borderColor: "#0c54a0",
                  backgroundColor: "#f0f6ff",
                },
              }}
            >
              Export to Excel
            </LoadingButton>
            <LoadingButton
              size="small"
              variant="contained"
              startIcon={<ArrowBackIosIcon sx={{ fontSize: 12 }} />}
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: 1,
                px: 2,
                py: 0.5,
                fontWeight: 600,
                fontSize: 13,
                lineHeight: 1.4,
                textTransform: "none",
              }}
            >
              Back
            </LoadingButton>
          </Grid>
        </Grid>

        <Grid className="form_design">
          <TableContainer
            component={Paper}
            sx={{ maxHeight: "calc(100vh - 260px)", minHeight: 300, overflowX: "auto" }}
          >
            <Table stickyHeader className="small_table" sx={{ minWidth: 2400 }}>
              <TableHead>
                <TableRow>
                  {columns.map((c) => (
                    <TableCell key={c.h} align={c.align} sx={NOWRAP}>
                      {c.h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={`${row.__label}-${row.id}`}>
                    {columns.map((c) => (
                      <TableCell key={c.h} align={c.align} sx={c.nowrap ? NOWRAP : undefined}>
                        {c.render(row)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
                {!loading && rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center">
                      No history found
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    </Layout>
  );
}

export default ProformaQuoteHistory;
